// Embed codes the Broadcaster Console offers for an event and the Video Wall.
//
// "script" codes use the EventLive loader (https://evt.live/embed.js, in
// eventlive-domain public/embed.js). It builds the iframe on the
// broadcaster's page and passes the player what a plain iframe can't see: the
// viewer's ?guest= id from an EventLive email link and the page's address.
// It also sizes the iframe. "iframe" codes are for site builders that strip
// scripts.

const POWERED_BY =
  '<a style="font-size:10px;font-family:sans-serif;color:#999;text-decoration:none;" href="https://www.eventlive.pro/">Powered by EventLive</a>';

const loaderScript = (base) => `<script async src="${base}embed.js"></script>`;

// The event page an iframe shows. `fit`: the page reports its height to the
// frame's parent, as it does for the loader, so the frame can follow it.
// `lang`: the page's language (evt.live reads ?lang=), only for the console's
// own preview; codes for websites leave it to each guest's browser.
function eventFrameSrc({
  base,
  broadcaster,
  slug,
  style = "embed",
  autoplay = true,
  reminders = true,
  liveOnly = false,
  fit = false,
  lang = null,
}) {
  const query = `autoplay=${autoplay}${reminders ? "" : "&rsvp_off=true"}${
    liveOnly ? "&liveonly=true" : ""
  }${fit ? "&fit=1" : ""}${lang ? `&lang=${encodeURIComponent(lang)}` : ""}`;
  const page = style === "embedBare" ? "embedBare" : "embed";
  return `${base}${broadcaster}/${slug}/${page}?${query}`;
}

// `base` is the viewer site with a trailing slash (https://evt.live/).
function eventEmbedCode({
  base,
  broadcaster,
  slug,
  style = "embed",
  autoplay = true,
  reminders = true,
  liveOnly = false,
  type = "script",
  lang = null,
}) {
  const bare = style === "embedBare";

  if (type === "script") {
    const attributes = [`data-eventlive="${broadcaster}/${slug}"`];
    if (bare) attributes.push('data-style="embedBare"');
    if (!autoplay) attributes.push('data-autoplay="false"');
    if (!reminders) attributes.push("data-rsvp-off");
    if (liveOnly) attributes.push("data-liveonly");
    // The link shows only where scripts don't run.
    return `<div ${attributes.join(
      " "
    )}><a href="${base}${broadcaster}/${slug}">Watch the event</a></div>${loaderScript(
      base
    )}${POWERED_BY}`;
  }

  const src = eventFrameSrc({
    base,
    broadcaster,
    slug,
    style,
    autoplay,
    reminders,
    liveOnly,
    lang,
  });

  // payment lets ticket buyers use Apple Pay in the embedded checkout (Safari)
  if (bare) {
    const allow = autoplay ? 'allow="autoplay; payment"' : 'allow="payment"';
    return `<div style="padding-bottom:56.25%;position:relative;margin:0 auto;"><iframe src="${src}" style="width:100%;height:100%;position:absolute;left:0px;top:0px;" width="100%" height="100%" allowfullscreen ${allow} allowtransparency="true" frameborder="0"></iframe></div>${POWERED_BY}`;
  }
  return `<div class="eventlive-embed"><iframe src="${src}" width="100%" height="100%" allowfullscreen allow="autoplay; payment" allowtransparency="true" frameborder="0"></iframe></div>${POWERED_BY}<style scoped> .eventlive-embed{padding-bottom: 56.25%; position: relative; margin: 0 auto;}.eventlive-embed iframe{width: 100%; height: 100%; position: absolute; left: 0px; top: 0px;}@media only screen and (max-width: 576px){.eventlive-embed{min-height: 630px; padding-bottom: 0;}.eventlive-embed iframe{position: static; min-height: 630px;}}</style>`;
}

function videoWallEmbedCode({ base, broadcaster, type = "script" }) {
  const wall = `${base}streams/${broadcaster}`;
  if (type === "script") {
    return `<div data-eventlive-wall="${broadcaster}"><a href="${wall}">Watch our live streams</a></div>${loaderScript(
      base
    )}`;
  }
  return `<iframe src="${wall}" width="100%" height="100%" style="margin:0 auto;" allowfullscreen allow="payment" allowtransparency="true" frameborder="0"></iframe>`;
}

module.exports = {
  POWERED_BY,
  eventEmbedCode,
  eventFrameSrc,
  videoWallEmbedCode,
};
